import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Playlist } from '@app/library/models/playlist.model';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { CheckListAddPlaylist } from '@radioking/onboarding-checklist';
import {
  OpenSpecificPanel,
  PANELS_KEYS,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { PerfectScrollbarDirective } from '@radioking/ui/perfect-scroll-bar';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';

import { PlaylistsState } from './../../../states/playlists.state';

@Component({
  selector: 'rk-playlist-list',
  templateUrl: './playlist-list.component.html',
  styleUrls: ['./playlist-list.component.scss'],
})
export class PlaylistListComponent implements OnInit, AfterViewInit {
  @ViewChild('container') container: ElementRef<HTMLDivElement>;
  @ViewChild(PerfectScrollbarDirective)
  scrollBar: PerfectScrollbarDirective;

  @Select(PlaylistsState.updatingPlaylistTracksCount)
  updatingPlaylist$: Observable<number>;

  @Input()
  radioId: number;

  @Input()
  set allPlaylists(items: Playlist[]) {
    if (
      this._allPlaylists &&
      items.length !== this._allPlaylists.length &&
      this.container &&
      this.container.nativeElement
    ) {
      this.container.nativeElement.scrollTop = 0;
    }
    this._allPlaylists = items;
    this.playlists = items;
    if (this.isSearching) {
      this.searchSubject.next(this.searchSubject.getValue());
    }
  }

  get allPlaylists(): Playlist[] {
    return this._allPlaylists;
  }

  private _allPlaylists: Playlist[];

  playlists: Playlist[];

  isSearching = false;

  searchSubject = new BehaviorSubject<string>('');

  scrollSubject = new BehaviorSubject<DragEvent>(undefined);

  pulse = false;

  constructor(
    private readonly store: Store,
    private readonly ngZone: NgZone,
    private readonly actions$: Actions,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.searchSubject
      .asObservable()
      .pipe(map(val => val.trim().toLowerCase()))
      .subscribe(data => {
        this.playlists = this.allPlaylists.filter(
          playlist => playlist.name.toLowerCase().indexOf(data) > -1,
        );
        if (this.container && this.container.nativeElement) {
          this.container.nativeElement.scrollTop = 0;
        }
      });

    this.scrollSubject
      .asObservable()
      .pipe(
        filter(e => !!e),
        throttleTime(70),
      )
      .subscribe(e => {
        const y = e.pageY - this.container.nativeElement.offsetTop;
        if (y > this.container.nativeElement.offsetHeight - 20) {
          this.scrollBar.scrollToY(this.container.nativeElement.scrollTop + 30, 250);
        } else if (y < 20) {
          this.scrollBar.scrollToY(this.container.nativeElement.scrollTop - 30, 250);
        }
      });

    this.actions$.pipe(ofActionDispatched(CheckListAddPlaylist)).subscribe(() => {
      this.pulse = true;
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    if (this.container && this.container.nativeElement) {
      this.container.nativeElement.scrollTop = 0;
      this.ngZone.runOutsideAngular(() => {
        fromEvent<DragEvent>(this.container.nativeElement, 'dragover').subscribe(e => {
          this.scrollSubject.next(e);
        });
      });
    }
  }

  toggleSearch() {
    this.isSearching = !this.isSearching;
  }

  search(e: string) {
    if (typeof e === 'string') {
      this.searchSubject.next(e);
    }
  }

  createPlaylist() {
    this.store.dispatch(
      new TryActionUnlessPanelIsBlocking(
        new OpenSpecificPanel(PANELS_KEYS.CREATE_PLAYLIST),
      ),
    );
  }
}
